import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage } from '@/lib/apiError';

import { useContadorProtocolo, useUpdateContadorProtocolo } from '../hooks/useConfiguracion';
import { CambioContadorConfirmacionDialog } from './CambioContadorConfirmacionDialog';

const contadorSchema = z.object({
  valor: z
    .number({ invalid_type_error: 'Ingresá un número válido' })
    .int('Debe ser un número entero')
    .min(0, 'Debe ser 0 o mayor'),
});

type ContadorFormValues = z.infer<typeof contadorSchema>;

export function ContadorProtocoloSection(): JSX.Element {
  const notify = useNotify();
  const query = useContadorProtocolo();
  const mutation = useUpdateContadorProtocolo();

  const [showDialog, setShowDialog] = useState(false);
  const [nuevoValorPendiente, setNuevoValorPendiente] = useState<number | null>(null);
  const [dialogError, setDialogError] = useState<unknown>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, errors },
  } = useForm<ContadorFormValues>({
    resolver: zodResolver(contadorSchema),
    defaultValues: { valor: 0 },
  });

  useEffect(() => {
    if (query.data) {
      reset({ valor: query.data.valor });
    }
  }, [query.data, reset]);

  const hasFormErrors = Object.keys(errors).length > 0;
  const valorActualEnDB = query.data?.valor ?? 0;

  function onSubmit(values: ContadorFormValues): void {
    if (values.valor < valorActualEnDB) {
      setError('valor', {
        type: 'manual',
        message: `El nuevo valor no puede ser menor al actual (${valorActualEnDB}). Si necesitás corregir esto, contactá a soporte.`,
      });
      return;
    }

    setNuevoValorPendiente(values.valor);
    setShowDialog(true);
  }

  async function handleConfirmar(): Promise<void> {
    if (nuevoValorPendiente === null) return;
    setDialogError(null);
    try {
      await mutation.mutateAsync({ valor: nuevoValorPendiente });
      notify.success('Numeración actualizada.');
      setShowDialog(false);
      setNuevoValorPendiente(null);
      reset({ valor: nuevoValorPendiente });
    } catch (err) {
      setDialogError(err);
    }
  }

  function handleCancelarDialog(): void {
    setShowDialog(false);
    setNuevoValorPendiente(null);
    setDialogError(null);
  }

  const infoText =
    query.data != null
      ? valorActualEnDB === 0
        ? 'Aún no se generó ningún protocolo. El primero será el N° 1.'
        : `Valor actual: ${valorActualEnDB}. El próximo protocolo será el N° ${valorActualEnDB + 1}.`
      : null;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Numeración de protocolos
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Número correlativo que se asigna al próximo protocolo creado. Modificar con precaución.
      </Typography>

      {query.isLoading ? (
        <Skeleton variant="rounded" height={40} sx={{ maxWidth: 240 }} />
      ) : query.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          }
        >
          {getErrorMessage(query.error)}
        </Alert>
      ) : (
        <>
          <Box sx={{ maxWidth: 240 }}>
            <Controller
              control={control}
              name="valor"
              render={({ field, fieldState }) => (
                <TextField
                  label="Contador actual *"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                  value={String(field.value)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') return;
                    const parsed = parseInt(v, 10);
                    if (!isNaN(parsed)) field.onChange(parsed);
                  }}
                  onBlur={field.onBlur}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>

          {infoText && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              {infoText}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              disabled={!isDirty || mutation.isPending || hasFormErrors}
              onClick={() => void handleSubmit(onSubmit)()}
              startIcon={
                mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {mutation.isPending ? 'Guardando...' : 'Guardar numeración'}
            </Button>
          </Box>
        </>
      )}

      <CambioContadorConfirmacionDialog
        open={showDialog}
        valorActual={valorActualEnDB}
        nuevoValor={nuevoValorPendiente ?? 0}
        isPending={mutation.isPending}
        error={dialogError}
        onConfirm={() => void handleConfirmar()}
        onCancel={handleCancelarDialog}
      />
    </Paper>
  );
}
